import React, { useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";

// Bespoke landing page for Aspenote — Offline Voice to Text (Android).
// Concept-first: the hero is the product's own demo, a recording being written
// down with the radio off. Real store screenshots in phone mockups, the six
// languages named rather than counted, and on-page SEO (meta + OG + JSON-LD
// SoftwareApplication / FAQPage). Hosted at /apps/aspenote.
//
// 🧨 NO RATING, ANYWHERE. Aspenote has zero reviews on Play as of 27 Aug 2026.
// Every other landing page here carries stars because the stars are real;
// inventing them for a new app is the one thing that would actually cost us,
// and `ratingLabel()` in appStats.ts exists precisely to make the absence
// automatic. There is no AggregateRating in the structured data either — a
// fabricated one is a Google penalty, not just a fib.

const PLAY =
  "https://play.google.com/store/apps/details?id=com.cosmovex.aspenote&referrer=" +
  encodeURIComponent(
    "utm_source=cosmovexapps&utm_medium=app_page&utm_campaign=aspenote"
  );
const SHOT = "/images/aspenote"; // 01..05.png — the real store screenshots
const LOGO = "/images/play/aspenote.png";

// Straight from the app's own design tokens: charcoal, terracotta, cream.
const CLAY = "#C15F2E";
const CLAY_D = "#9A4A21";
const INK = "#26221C";
const SUB = "#6E6759";
const CREAM = "#FAF8F3";
const CARD = "#FFFFFF";
const LINE = "#EAE4D8";

const SERIF = `"Literata", "Iowan Old Style", Georgia, "Times New Roman", serif`;
const SANS = `-apple-system, "SF Pro Display", "Segoe UI", Roboto, Helvetica, Arial, sans-serif`;
const MONO = `"SF Mono", "JetBrains Mono", ui-monospace, Menlo, Consolas, monospace`;

const playClick = (e: React.MouseEvent) => {
  e.preventDefault();
  const w = window as any;
  if (w.gtag_report_conversion) w.gtag_report_conversion(PLAY);
  else window.open(PLAY, "_blank", "noopener");
};

const PlayButton: React.FC<{ small?: boolean }> = ({ small }) => (
  <a
    href={PLAY}
    onClick={playClick}
    aria-label="Get Aspenote on Google Play"
    style={{
      display: "inline-flex",
      alignItems: "center",
      gap: "0.6rem",
      padding: small ? "0.72rem 1.35rem" : "1.02rem 1.9rem",
      background: `linear-gradient(180deg, ${CLAY} 0%, ${CLAY_D} 100%)`,
      color: "#fff",
      borderRadius: "0.9rem",
      fontWeight: 700,
      fontSize: small ? "0.98rem" : "1.14rem",
      textDecoration: "none",
      border: "1px solid rgba(0,0,0,0.06)",
      boxShadow:
        "0 1px 2px rgba(90,45,18,0.16), 0 10px 24px -8px rgba(154,74,33,0.45)",
      fontFamily: SANS,
      letterSpacing: "-0.2px",
    }}
  >
    <svg width={small ? 18 : 22} height={small ? 18 : 22} viewBox="0 0 24 24" fill="currentColor">
      <path d="M3.609 1.814L13.792 12 3.61 22.186a.996.996 0 01-.61-.92V2.734a1 1 0 01.609-.92zm10.89 10.893l2.302 2.302-10.937 6.333 8.635-8.635zm3.199-3.2l2.807 1.626a1 1 0 010 1.734l-2.808 1.626L15.206 12l2.492-2.493zM5.864 2.658L16.8 8.99l-2.302 2.302-8.634-8.634z" />
    </svg>
    Get it on Google Play
  </a>
);

/* ── The hero: a recording being written down, with the radio off ─────────── */

const LINE_TEXT =
  "I keep meaning to say this and forgetting — let's just decide on Friday.";

const Recorder: React.FC = () => {
  const [n, setN] = useState(0);
  const [done, setDone] = useState(false);

  useEffect(() => {
    // Respect a reduced-motion preference by showing the finished state.
    const reduce =
      typeof window !== "undefined" &&
      window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
    if (reduce) {
      setN(LINE_TEXT.length);
      setDone(true);
      return;
    }
    let i = 0;
    const type = setInterval(() => {
      i += 1;
      setN(i);
      if (i >= LINE_TEXT.length) {
        clearInterval(type);
        setTimeout(() => setDone(true), 700);
      }
    }, 42);
    return () => clearInterval(type);
  }, []);

  const secs = Math.min(7, Math.round((n / LINE_TEXT.length) * 7));

  return (
    <div
      style={{
        background: INK,
        borderRadius: "1.25rem",
        padding: "1.4rem 1.5rem 1.6rem",
        boxShadow: "0 30px 60px -30px rgba(38,34,28,0.55)",
        border: "1px solid rgba(255,255,255,0.07)",
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          fontFamily: MONO,
          fontSize: "0.72rem",
          letterSpacing: "1.2px",
          color: "rgba(250,248,243,0.55)",
        }}
      >
        <span style={{ display: "inline-flex", alignItems: "center", gap: "0.5rem" }}>
          <span
            style={{
              width: 8,
              height: 8,
              borderRadius: 8,
              background: CLAY,
              boxShadow: `0 0 0 4px rgba(193,95,46,0.18)`,
            }}
          />
          <span style={{ color: CLAY, fontWeight: 600 }}>RECORDING</span>
          <span>0:0{secs}</span>
        </span>
        {/* The claim the whole product rests on, stated where it is being made. */}
        <span style={{ display: "inline-flex", alignItems: "center", gap: "0.4rem" }}>
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="rgba(250,248,243,0.5)" strokeWidth="2" strokeLinecap="round">
            <path d="M17.56 17.56A9 9 0 015 5m2.5-1.9A9 9 0 0119 15.5M2 2l20 20" />
          </svg>
          NO CONNECTION
        </span>
      </div>

      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 3,
          height: 46,
          margin: "1.05rem 0 1.15rem",
        }}
      >
        {Array.from({ length: 56 }).map((_, i) => {
          const active = i / 56 <= n / LINE_TEXT.length;
          const h = 8 + Math.abs(Math.sin(i * 0.7)) * 30 + (i % 5) * 2;
          return (
            <span
              key={i}
              style={{
                flex: 1,
                height: h,
                borderRadius: 3,
                background: active ? CLAY : "rgba(250,248,243,0.13)",
                transition: "background 220ms ease",
              }}
            />
          );
        })}
      </div>

      <p
        style={{
          fontFamily: SANS,
          fontSize: "1.12rem",
          lineHeight: 1.55,
          color: "#FAF8F3",
          margin: 0,
          minHeight: "5.2rem",
        }}
      >
        {LINE_TEXT.slice(0, n)}
        {!done && (
          <span style={{ opacity: 0.65, color: CLAY }}>▋</span>
        )}
      </p>

      <div
        style={{
          marginTop: "1.2rem",
          paddingTop: "1.1rem",
          borderTop: "1px solid rgba(250,248,243,0.1)",
          fontFamily: MONO,
          fontSize: "0.74rem",
          letterSpacing: "0.6px",
          color: done ? CLAY : "rgba(250,248,243,0.4)",
          transition: "color 400ms ease",
        }}
      >
        {done ? "NOTE READY — SUMMARY, ACTIONS, FULL TRANSCRIPT" : "Writing your notes…"}
      </div>
    </div>
  );
};

const Phone: React.FC<{ src: string; alt: string; w?: number }> = ({ src, alt, w = 232 }) => (
  <div
    style={{
      width: w,
      borderRadius: "1.6rem",
      overflow: "hidden",
      background: CARD,
      border: `1px solid ${LINE}`,
      boxShadow: "0 22px 44px -26px rgba(38,34,28,0.4)",
      flex: "0 0 auto",
    }}
  >
    <img src={src} alt={alt} loading="lazy" style={{ width: "100%", display: "block" }} />
  </div>
);

const SHOTS: { src: string; label: string; sub: string }[] = [
  { src: "01.png", label: "Every note, on the phone", sub: "Searchable, and yours" },
  { src: "02.png", label: "Press one button", sub: "It keeps going through a call" },
  { src: "03.png", label: "A summary, and what you owe", sub: "The full transcript underneath" },
  { src: "04.png", label: "Words as you speak", sub: "Written by the phone itself" },
  { src: "05.png", label: "One payment, not a subscription", sub: "Two complete notes free" },
];

// The six languages the model was actually tested on. See
// packages/aspenote_engine/lib/src/speech_languages.dart in the app repo — this
// list is a measurement, not a copy of the model's own table.
const LANGS = ["English", "Français", "Deutsch", "Italiano", "Português", "Nederlands"];

const FAQ: { q: string; a: string }[] = [
  {
    q: "Does Aspenote work without an internet connection?",
    a: "Yes. Transcription, the summary and translation all run on the phone itself. After a one-time setup download the app never needs a connection to turn a recording into a note — it works on a plane, in a basement, or with mobile data switched off entirely.",
  },
  {
    q: "Is my recording uploaded anywhere?",
    a: "No. No part of a recording leaves the device — not the audio, not the transcript, not the summary. There is no account and no sign-up, and because transcription happens on the phone there is no server to send anything to.",
  },
  {
    q: "Which languages does it transcribe?",
    a: "English, French, German, Italian, Portuguese and Dutch. We name them rather than advertising a number, because we tested each one on the speech model the app actually ships with. Translating a finished note works into many more languages than that — translating and transcribing are two different things.",
  },
  {
    q: "What is the first-run download?",
    a: "About 160 MB of speech model, fetched once over Wi-Fi the first time you open the app. It is the only time Aspenote needs a connection. You can start recording straight away; the note finishes as soon as the model lands.",
  },
  {
    q: "What do I get for free?",
    a: "Your first two notes are complete at any length — full transcript, full summary, nothing blurred and nothing behind a padlock. After that you get five notes a day of up to five minutes each, and the count starts again every morning.",
  },
  {
    q: "Is Pro a subscription?",
    a: "No. Aspenote Pro is a single payment that removes the length and count limits for good — there is nothing to renew and nothing to cancel. A monthly plan is offered as well for anyone who would rather pay that way.",
  },
  {
    q: "Does it keep recording if I take a call or lock the screen?",
    a: "Yes. Recording continues through a phone call and a locked screen, and audio is written to storage as it is captured, so an interruption costs seconds rather than the whole recording. Close the app and the note still finishes on its own.",
  },
  {
    q: "Can I use a recording I already have?",
    a: "Yes. Import a voice memo, an interview or a lecture somebody sent you and get text back. Imported audio is transcribed on the phone too — importing does not quietly send it somewhere.",
  },
];

const Eyebrow: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <p
    style={{
      fontFamily: MONO,
      fontSize: "0.74rem",
      letterSpacing: "1.6px",
      textTransform: "uppercase",
      color: CLAY,
      margin: "0 0 0.75rem",
      fontWeight: 600,
    }}
  >
    {children}
  </p>
);

const AspenoteLanding: React.FC = () => {
  const [showBar, setShowBar] = useState(false);
  const [open, setOpen] = useState<number | null>(0);

  useEffect(() => {
    const onScroll = () => setShowBar(window.scrollY > 640);
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const softwareLd = {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    name: "Aspenote — Offline Voice to Text",
    operatingSystem: "Android",
    applicationCategory: "ProductivityApplication",
    // 🧨 Free to install; Pro is a one-time in-app purchase. No aggregateRating
    // — the app has no reviews yet and inventing one is a penalty, not a fib.
    offers: { "@type": "Offer", price: "0", priceCurrency: "USD" },
    description:
      "Record and transcribe on your phone, offline. Aspenote turns a recording into a summary, the things to do and a full transcript without uploading anything and without an account.",
    downloadUrl: PLAY,
    image: "https://cosmovexapps.com" + LOGO,
  };

  const faqLd = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: FAQ.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  };

  return (
    <div style={{ background: CREAM, color: INK, fontFamily: SANS }}>
      <Helmet>
        <title>Aspenote — Offline Voice to Text &amp; AI Notes for Android</title>
        <meta
          name="description"
          content="Record and transcribe on your phone, offline. A summary, the things to do and the full transcript — nothing uploaded, no account, and it works with no connection at all."
        />
        <link rel="canonical" href="https://cosmovexapps.com/apps/aspenote" />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://cosmovexapps.com/apps/aspenote" />
        <meta property="og:title" content="Aspenote — Offline Voice to Text for Android" />
        <meta
          property="og:description"
          content="Transcription that runs on your phone. Nothing uploaded, no account, works with no connection."
        />
        <meta property="og:image" content={"https://cosmovexapps.com" + LOGO} />
        <meta name="twitter:card" content="summary_large_image" />
        <script type="application/ld+json">{JSON.stringify(softwareLd)}</script>
        <script type="application/ld+json">{JSON.stringify(faqLd)}</script>
      </Helmet>

      {/* Sticky install bar, once the hero is behind you */}
      <div
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          zIndex: 60,
          background: "rgba(250,248,243,0.92)",
          backdropFilter: "blur(10px)",
          borderBottom: `1px solid ${LINE}`,
          transform: showBar ? "translateY(0)" : "translateY(-110%)",
          transition: "transform 260ms ease",
        }}
      >
        <div
          style={{
            maxWidth: 1120,
            margin: "0 auto",
            padding: "0.7rem 1.5rem",
            display: "flex",
            alignItems: "center",
            gap: "0.9rem",
          }}
        >
          <img src={LOGO} alt="" width={34} height={34} style={{ borderRadius: 9 }} />
          <strong style={{ fontSize: "1rem", letterSpacing: "-0.2px" }}>Aspenote</strong>
          <span style={{ color: SUB, fontSize: "0.92rem" }} className="hide-sm">
            Offline voice to text
          </span>
          <span style={{ marginLeft: "auto" }}>
            <PlayButton small />
          </span>
        </div>
      </div>

      {/* ── Hero ─────────────────────────────────────────────────────────── */}
      <section
        style={{
          maxWidth: 1120,
          margin: "0 auto",
          padding: "4.5rem 1.5rem 3rem",
          display: "grid",
          gap: "3rem",
          gridTemplateColumns: "minmax(0,1fr)",
        }}
        className="aspen-hero"
      >
        <div>
          <div style={{ display: "flex", alignItems: "center", gap: "0.85rem", marginBottom: "1.6rem" }}>
            <img src={LOGO} alt="Aspenote" width={56} height={56} style={{ borderRadius: 14 }} />
            <div>
              <div style={{ fontWeight: 700, fontSize: "1.05rem" }}>Aspenote</div>
              <div style={{ color: SUB, fontSize: "0.9rem" }}>Free on Google Play · Android</div>
            </div>
          </div>

          <h1
            style={{
              fontFamily: SERIF,
              fontSize: "clamp(2.4rem, 5.6vw, 3.9rem)",
              lineHeight: 1.06,
              letterSpacing: "-1px",
              margin: "0 0 1.1rem",
            }}
          >
            It writes it down.
            <br />
            <span style={{ color: CLAY }}>On your phone.</span>
          </h1>

          <p style={{ fontSize: "1.16rem", lineHeight: 1.6, color: SUB, margin: "0 0 1.8rem", maxWidth: 540 }}>
            A conversation, a lecture, a thought at midnight — recorded and written
            up on the device itself. A summary, the things to do, and the whole
            transcript. Nothing is uploaded, there is no account, and it works with
            no connection at all.
          </p>

          <PlayButton />

          <ul
            style={{
              listStyle: "none",
              padding: 0,
              margin: "1.7rem 0 0",
              display: "flex",
              flexWrap: "wrap",
              gap: "0.6rem",
            }}
          >
            {["No account", "Nothing uploaded", "Works offline", "No ads"].map((p) => (
              <li
                key={p}
                style={{
                  fontSize: "0.88rem",
                  color: SUB,
                  border: `1px solid ${LINE}`,
                  background: CARD,
                  borderRadius: 999,
                  padding: "0.42rem 0.9rem",
                }}
              >
                {p}
              </li>
            ))}
          </ul>
        </div>

        <div>
          <Recorder />
        </div>
      </section>

      {/* ── What comes back ──────────────────────────────────────────────── */}
      <section style={{ maxWidth: 1120, margin: "0 auto", padding: "2.5rem 1.5rem 1rem" }}>
        <Eyebrow>What comes back</Eyebrow>
        <h2 style={{ fontFamily: SERIF, fontSize: "clamp(1.9rem,3.6vw,2.6rem)", margin: "0 0 2.2rem", letterSpacing: "-0.6px" }}>
          One recording, three things you can use.
        </h2>
        <div style={{ display: "grid", gap: "1.1rem", gridTemplateColumns: "repeat(auto-fit,minmax(260px,1fr))" }}>
          {[
            { h: "A summary", p: "What was actually discussed, in the language it was discussed in — not a translation of it." },
            { h: "The things to do", p: "The commitments that came out of it, pulled from the transcript rather than invented around it." },
            { h: "The full transcript", p: "Underneath, complete, never truncated — however long the recording ran." },
          ].map((c) => (
            <div key={c.h} style={{ background: CARD, border: `1px solid ${LINE}`, borderRadius: "1rem", padding: "1.5rem 1.4rem" }}>
              <h3 style={{ fontFamily: SERIF, fontSize: "1.3rem", margin: "0 0 0.55rem" }}>{c.h}</h3>
              <p style={{ margin: 0, color: SUB, lineHeight: 1.6, fontSize: "0.98rem" }}>{c.p}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ── Screenshots ──────────────────────────────────────────────────── */}
      <section style={{ padding: "3.5rem 0 1rem" }}>
        <div style={{ maxWidth: 1120, margin: "0 auto", padding: "0 1.5rem" }}>
          <Eyebrow>On the phone</Eyebrow>
          <h2 style={{ fontFamily: SERIF, fontSize: "clamp(1.9rem,3.6vw,2.6rem)", margin: "0 0 1.8rem", letterSpacing: "-0.6px" }}>
            The whole app, not a mockup of it.
          </h2>
        </div>
        <div
          style={{
            display: "flex",
            gap: "1.1rem",
            overflowX: "auto",
            padding: "0.5rem 1.5rem 1.5rem",
            scrollSnapType: "x mandatory",
          }}
        >
          {SHOTS.map((s) => (
            <div key={s.src} style={{ scrollSnapAlign: "start" }}>
              <Phone src={`${SHOT}/${s.src}`} alt={s.label} />
              <div style={{ width: 232, marginTop: "0.8rem" }}>
                <div style={{ fontWeight: 600, fontSize: "0.95rem" }}>{s.label}</div>
                <div style={{ color: SUB, fontSize: "0.88rem" }}>{s.sub}</div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* ── Languages ────────────────────────────────────────────────────── */}
      <section style={{ background: CARD, borderTop: `1px solid ${LINE}`, borderBottom: `1px solid ${LINE}`, marginTop: "2rem" }}>
        <div style={{ maxWidth: 900, margin: "0 auto", padding: "4rem 1.5rem", textAlign: "center" }}>
          <Eyebrow>The languages it works in</Eyebrow>
          <h2 style={{ fontFamily: SERIF, fontSize: "clamp(1.9rem,3.8vw,2.7rem)", margin: "0 0 1.1rem", letterSpacing: "-0.6px" }}>
            Named, not counted.
          </h2>
          <p style={{ color: SUB, lineHeight: 1.65, fontSize: "1.05rem", maxWidth: 640, margin: "0 auto 2rem" }}>
            Most transcription apps advertise a number taken from the model's own
            documentation rather than from anyone sitting down and trying it. Every
            language below was tested on the model Aspenote actually ships with,
            with more than one voice, before it went on the list.
          </p>
          <ul style={{ listStyle: "none", padding: 0, margin: "0 0 1.6rem", display: "flex", flexWrap: "wrap", gap: "0.55rem", justifyContent: "center" }}>
            {LANGS.map((l) => (
              <li
                key={l}
                style={{
                  fontFamily: SERIF,
                  fontSize: "1.06rem",
                  border: `1px solid ${LINE}`,
                  borderRadius: 999,
                  padding: "0.5rem 1.15rem",
                  background: CREAM,
                }}
              >
                {l}
              </li>
            ))}
          </ul>
          <p style={{ color: SUB, fontSize: "0.94rem", margin: 0 }}>
            A finished note can be <strong style={{ color: INK }}>translated</strong> into
            many more languages than that, on the device. Translating and
            transcribing are two different things.
          </p>
        </div>
      </section>

      {/* ── Privacy ──────────────────────────────────────────────────────── */}
      <section style={{ maxWidth: 1120, margin: "0 auto", padding: "4rem 1.5rem 2rem" }}>
        <div style={{ display: "grid", gap: "2.5rem", gridTemplateColumns: "repeat(auto-fit,minmax(300px,1fr))", alignItems: "center" }}>
          <div>
            <Eyebrow>Your recordings</Eyebrow>
            <h2 style={{ fontFamily: SERIF, fontSize: "clamp(1.9rem,3.6vw,2.6rem)", margin: "0 0 1rem", letterSpacing: "-0.6px" }}>
              There is nowhere for it to go.
            </h2>
            <p style={{ color: SUB, lineHeight: 1.65, fontSize: "1.05rem", margin: "0 0 1rem" }}>
              Transcription runs on the phone, so no part of a recording is sent
              anywhere — not the audio, not the transcript, not the summary. That is
              a stronger statement than a promise not to look: there is nothing to
              look at.
            </p>
            <p style={{ color: SUB, lineHeight: 1.65, fontSize: "1.05rem", margin: 0 }}>
              For a conversation under an NDA, a consultation, a client interview or
              a session with a patient, private transcription is the difference
              between a tool you are allowed to use and one you are not.
            </p>
          </div>
          <div style={{ background: INK, color: CREAM, borderRadius: "1.2rem", padding: "1.8rem 1.7rem" }}>
            <div style={{ fontFamily: MONO, fontSize: "0.72rem", letterSpacing: "1.4px", color: "rgba(250,248,243,0.5)", marginBottom: "1.1rem" }}>
              WHAT LEAVES THE PHONE
            </div>
            {[
              ["Your audio", "never"],
              ["The transcript", "never"],
              ["The summary", "never"],
              ["Your name or email", "not asked for"],
              ["The speech model", "downloaded once"],
            ].map(([k, v]) => (
              <div key={k} style={{ display: "flex", justifyContent: "space-between", gap: "1rem", padding: "0.6rem 0", borderBottom: "1px solid rgba(250,248,243,0.1)" }}>
                <span style={{ fontSize: "0.98rem" }}>{k}</span>
                <span style={{ fontFamily: MONO, fontSize: "0.85rem", color: CLAY }}>{v}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Pricing ──────────────────────────────────────────────────────── */}
      <section style={{ maxWidth: 900, margin: "0 auto", padding: "3rem 1.5rem 1rem", textAlign: "center" }}>
        <Eyebrow>Pricing</Eyebrow>
        <h2 style={{ fontFamily: SERIF, fontSize: "clamp(1.9rem,3.8vw,2.7rem)", margin: "0 0 2rem", letterSpacing: "-0.6px" }}>
          Two complete notes, free.
        </h2>
        <div style={{ display: "grid", gap: "1.1rem", gridTemplateColumns: "repeat(auto-fit,minmax(260px,1fr))", textAlign: "left" }}>
          <div style={{ background: CARD, border: `1px solid ${LINE}`, borderRadius: "1rem", padding: "1.6rem 1.5rem" }}>
            <div style={{ fontFamily: MONO, fontSize: "0.74rem", letterSpacing: "1.4px", color: SUB }}>FREE</div>
            <div style={{ fontFamily: SERIF, fontSize: "2.4rem", margin: "0.3rem 0 0.2rem" }}>$0</div>
            <div style={{ color: SUB, fontSize: "0.92rem", marginBottom: "1.1rem" }}>no account, no card</div>
            {["First two notes complete, any length", "Then five a day, five minutes each", "Full summary and transcript, always"].map((f) => (
              <div key={f} style={{ color: SUB, fontSize: "0.96rem", padding: "0.35rem 0" }}>· {f}</div>
            ))}
          </div>
          <div style={{ background: CARD, border: `1.5px solid ${CLAY}`, borderRadius: "1rem", padding: "1.6rem 1.5rem" }}>
            <div style={{ fontFamily: MONO, fontSize: "0.74rem", letterSpacing: "1.4px", color: CLAY }}>PRO</div>
            <div style={{ fontFamily: SERIF, fontSize: "2.4rem", margin: "0.3rem 0 0.2rem" }}>$29.99</div>
            <div style={{ color: SUB, fontSize: "0.92rem", marginBottom: "1.1rem" }}>once — not a subscription</div>
            {["Record for as long as you need", "As many notes as you like, every day", "Import recordings without a limit", "Or $4.99 a month, if you would rather"].map((f) => (
              <div key={f} style={{ color: SUB, fontSize: "0.96rem", padding: "0.35rem 0" }}>· {f}</div>
            ))}
          </div>
        </div>
      </section>

      {/* ── FAQ ──────────────────────────────────────────────────────────── */}
      <section style={{ maxWidth: 780, margin: "0 auto", padding: "4rem 1.5rem 1rem" }}>
        <Eyebrow>Questions</Eyebrow>
        <h2 style={{ fontFamily: SERIF, fontSize: "clamp(1.9rem,3.6vw,2.6rem)", margin: "0 0 1.8rem", letterSpacing: "-0.6px" }}>
          The things people ask.
        </h2>
        {FAQ.map((f, i) => (
          <div key={f.q} style={{ borderBottom: `1px solid ${LINE}` }}>
            <button
              onClick={() => setOpen(open === i ? null : i)}
              aria-expanded={open === i}
              style={{
                width: "100%",
                textAlign: "left",
                background: "none",
                border: "none",
                padding: "1.15rem 0",
                cursor: "pointer",
                fontSize: "1.04rem",
                fontWeight: 600,
                color: INK,
                display: "flex",
                justifyContent: "space-between",
                gap: "1rem",
                fontFamily: SANS,
              }}
            >
              {f.q}
              <span style={{ color: CLAY, flex: "0 0 auto" }}>{open === i ? "−" : "+"}</span>
            </button>
            {open === i && (
              <p style={{ margin: "0 0 1.25rem", color: SUB, lineHeight: 1.65 }}>{f.a}</p>
            )}
          </div>
        ))}
      </section>

      {/* ── Close ────────────────────────────────────────────────────────── */}
      <section style={{ padding: "3.5rem 1.5rem 5rem", textAlign: "center" }}>
        <h2 style={{ fontFamily: SERIF, fontSize: "clamp(2rem,4vw,2.8rem)", margin: "0 0 0.8rem", letterSpacing: "-0.6px" }}>
          Record the next one.
        </h2>
        <p style={{ color: SUB, margin: "0 0 1.8rem", fontSize: "1.05rem" }}>
          Two complete notes, free. No account, no card.
        </p>
        <PlayButton />
        <p style={{ color: SUB, fontSize: "0.88rem", marginTop: "1.6rem" }}>
          <a href="https://aspenote.com/privacy" style={{ color: SUB }}>Privacy</a>
          {" · "}
          <a href="https://aspenote.com/terms" style={{ color: SUB }}>Terms</a>
          {" · "}
          <a href="https://aspenote.com/support" style={{ color: SUB }}>Support</a>
        </p>
      </section>

      <style>{`
        @media (min-width: 900px) {
          .aspen-hero { grid-template-columns: 1.05fr 0.95fr !important; align-items: center; }
        }
        @media (max-width: 640px) { .hide-sm { display: none; } }
      `}</style>
    </div>
  );
};

export default AspenoteLanding;
